/* eslint-disable @typescript-eslint/no-this-alias */

/**
 * A function that can be called with any arguments and returns void.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Procedure = (...args: any[]) => any;

/**
 * A debounced function with additional methods for control.
 */
export interface DebouncedFunction<F extends Procedure> {
  (
    this: ThisParameterType<F>,
    ...args: Parameters<F>
  ): ReturnType<F> | undefined;
  /**
   * Cancels any pending invocation of the debounced function.
   */
  cancel: () => void;
  /**
   * Immediately invokes the debounced function if there's a pending invocation.
   */
  flush: () => ReturnType<F> | undefined;
}

/**
 * Configuration options for controlling the behavior of the debounced function.
 */
export interface DebounceOptions {
  /**
   * Determines whether the function should be invoked on the leading edge of the timeout.
   * @default false
   */
  leading?: boolean;
  /**
   * Determines whether the function should be invoked on the trailing edge of the timeout.
   * @default true
   */
  trailing?: boolean;
  /**
   * The maximum time the specified function is allowed to be delayed before it is invoked.
   */
  maxWait?: number;
}

/**
 * Error message for invalid function parameter.
 */
const FUNC_ERROR_TEXT = 'Expected a function';

/**
 * Converts a value to a number.
 */
function toNumber(value: unknown): number {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  }
  return 0;
}

/**
 * Checks if a value is an object (not null and typeof 'object').
 */
function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

/**
 * Gets the current timestamp.
 */
function now(): number {
  return Date.now();
}

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds
 * have elapsed since the last time the debounced function was invoked.
 *
 * The debounced function comes with a cancel method to cancel delayed func invocations
 * and a flush method to immediately invoke them.
 *
 * @param func - The function to debounce
 * @param wait - The number of milliseconds to delay
 * @param options - The options object
 * @returns The new debounced function
 */
export function debounce<F extends Procedure>(
  func: F,
  wait: number = 0,
  options: DebounceOptions = {},
): DebouncedFunction<F> {
  // Validate the function parameter
  if (typeof func !== 'function') {
    throw new TypeError(FUNC_ERROR_TEXT);
  }

  // Convert wait to a number and ensure it's not negative
  const waitMs = Math.max(toNumber(wait) || 0, 0);

  // Extract and validate options
  const validOptions = isObject(options) ? options : {};
  const {
    leading = false,
    trailing = true,
    maxWait = undefined,
  } = validOptions;

  // Determine if we're using maxWait functionality
  const useMaxWait = maxWait !== undefined;
  const maxWaitMs = useMaxWait ? Math.max(toNumber(maxWait) || 0, waitMs) : 0;

  // State variables for tracking function calls and timing
  let lastArgs: Parameters<F> | undefined;
  let lastThis: ThisParameterType<F> | undefined;
  let result: ReturnType<F> | undefined;
  let timerId: ReturnType<typeof setTimeout> | undefined;
  let lastCallTime: number | undefined;
  let lastInvokeTime = 0;

  /**
   * Invokes the original function with the stored context and arguments.
   */
  function invokeFunc(time: number): ReturnType<F> {
    const args = lastArgs!;
    const thisArg = lastThis;

    // Clear the stored arguments and context
    lastArgs = lastThis = undefined;
    lastInvokeTime = time;

    // Invoke the function and store the result
    result = func.apply(thisArg, args);
    return result as ReturnType<F>;
  }

  /**
   * Handles the leading edge of the debounce timeout.
   */
  function startLeadingEdge(time: number): ReturnType<F> | undefined {
    // Reset the maxWait timer
    lastInvokeTime = time;

    // Start the timer for the trailing edge
    timerId = setTimeout(onTimerExpired, waitMs);

    // Invoke immediately if leading edge is enabled
    return leading ? invokeFunc(time) : result;
  }

  /**
   * Calculates the remaining wait time before the next invocation.
   */
  function getRemainingWait(time: number): number {
    const timeSinceLastCall = time - lastCallTime!;
    const timeSinceLastInvoke = time - lastInvokeTime;
    const timeWaiting = waitMs - timeSinceLastCall;

    return useMaxWait
      ? Math.min(timeWaiting, maxWaitMs - timeSinceLastInvoke)
      : timeWaiting;
  }

  /**
   * Determines if the function should be invoked based on timing conditions.
   */
  function shouldInvoke(time: number): boolean {
    if (lastCallTime === undefined) {
      return true; // First call
    }

    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;

    // Function should be invoked if:
    // 1. Enough time has passed since the last call
    // 2. Time went backwards (system clock adjustment)
    // 3. We've hit the maxWait limit
    return (
      timeSinceLastCall >= waitMs ||
      timeSinceLastCall < 0 ||
      (useMaxWait && timeSinceLastInvoke >= maxWaitMs)
    );
  }

  /**
   * Handles timer expiration - decides whether to invoke or restart the timer.
   */
  function onTimerExpired(): ReturnType<F> | undefined {
    const time = now();

    if (shouldInvoke(time)) {
      return handleTrailingEdge(time);
    }

    // Restart the timer with the remaining wait time
    timerId = setTimeout(onTimerExpired, getRemainingWait(time));
    return undefined;
  }

  /**
   * Handles the trailing edge of the debounce timeout.
   */
  function handleTrailingEdge(time: number): ReturnType<F> | undefined {
    timerId = undefined;

    // Only invoke if we have arguments (meaning the function was called at least once)
    // and trailing edge is enabled
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }

    // Clean up stored arguments and context
    lastArgs = lastThis = undefined;
    return result;
  }

  /**
   * Cancels any pending invocation of the debounced function.
   */
  function cancel(): void {
    if (timerId !== undefined) {
      clearTimeout(timerId);
    }

    // Reset all state
    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timerId = undefined;
  }

  /**
   * Immediately invokes the debounced function if there's a pending invocation.
   */
  function flush(): ReturnType<F> | undefined {
    return timerId === undefined ? result : handleTrailingEdge(now());
  }

  /**
   * The main debounced function that gets returned.
   */
  function debounced(
    this: ThisParameterType<F>,
    ...args: Parameters<F>
  ): ReturnType<F> | undefined {
    const time = now();
    const isInvoking = shouldInvoke(time);

    // Store the current call's arguments and context
    lastArgs = args;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timerId === undefined) {
        // No timer is running, start the leading edge
        return startLeadingEdge(lastCallTime);
      }

      if (useMaxWait) {
        // Handle invocations in a tight loop when maxWait is set
        clearTimeout(timerId);
        timerId = setTimeout(onTimerExpired, waitMs);
        return invokeFunc(lastCallTime);
      }
    }

    // Start a timer if one isn't already running
    if (timerId === undefined) {
      timerId = setTimeout(onTimerExpired, waitMs);
    }

    return result;
  }

  // Attach control methods to the debounced function
  debounced.cancel = cancel;
  debounced.flush = flush;

  return debounced;
}
